import { useLazyQuery, useMutation, useQuery } from '@apollo/client/react';
import {
  STAFF_BILLING_UPDATE_COMPANY_SCREENS,
  STAFF_BILLING_UPDATE_TEAM_SCREENS,
  STAFF_BILLING_NORMAL_ONBOARDING,
  STAFF_BILLING_USERFUL_ONBOARDING,
  STAFF_BILLING_SET_INVOICE_STATUS,
  STAFF_BILLING_HARDWARE_CATALOG,
  STAFF_BILLING_CREATE_HARDWARE_INVOICE,
  STAFF_BILLING_PLAN_CATALOG,
  STAFF_BILLING_STRIPE_CUSTOMER,
  STAFF_BILLING_SET_DUNNING_EXCLUSION,
  STAFF_BILLING_CREATE_STRIPE_SUBSCRIPTION,
  STAFF_BILLING_SUBSCRIPTION_SNAPSHOT,
  STAFF_BILLING_CHANGE_SUBSCRIPTION,
  STAFF_BILLING_COUPON_CATALOG,
  STAFF_BILLING_COUPON_PREVIEW,
  STAFF_BILLING_APPLY_COUPON,
  STAFF_BILLING_REMOVE_COUPON,
} from '@staff-app/graphql-client';
import type { SalesActionResult } from './useSalesConsole';

export function useBillingUpdateCompanyScreens() {
  return useMutation<
    { staffBillingUpdateCompanyScreens: SalesActionResult },
    { stripeId: string; totalQuantity: number; previousQuantity?: number }
  >(STAFF_BILLING_UPDATE_COMPANY_SCREENS);
}

export function useBillingUpdateTeamScreens() {
  return useMutation<
    { staffBillingUpdateTeamScreens: SalesActionResult },
    { stripeId: string; totalQuantity: number; previousQuantity?: number }
  >(STAFF_BILLING_UPDATE_TEAM_SCREENS);
}

/**
 * One add-on grant. Becomes an entry in the company's
 * `payment.subscriptionIdAddOns` — the same array the customer-facing Account
 * Add-Ons panel reads.
 */
export type NormalOnboardingAddOn = {
  /** Catalog key, e.g. `videowall` — see ADD_ON_CATALOG in BillingConsole. */
  key: string;
  /** Licensed screens; omitted for non-quantified add-ons (Custom Domain). */
  maxQuantity?: number;
  /** Stripe subscription-item id (`si_…`) for this add-on's line. Required. */
  subscriptionItemId: string;
  /** Tier, tiered add-ons only (OptiSound: standard | pro). */
  tier?: string;
  /** OptiDev only. */
  enableAllScreens?: boolean;
};

export type NormalOnboardingVars = {
  stripeId: string;
  subscriptionId: string;
  subQty: number;
  subscriptionNormalId: string;
  invoiceId: string;
  planFeature: string;
  currency: string;
  addOns?: NormalOnboardingAddOn[];
};

export function useBillingNormalOnboarding() {
  return useMutation<{ staffBillingNormalOnboarding: SalesActionResult }, NormalOnboardingVars>(
    STAFF_BILLING_NORMAL_ONBOARDING,
  );
}

export type UserfulOnboardingVars = {
  stripeId: string;
  subscriptionId: string;
  subQty: number;
  subscriptionNormalId: string;
  planFeature: string;
  email: string;
  poNumber: string;
  contractPeriod: string;
  purchaseAmount: string;
  licenseEndDate: string;
  invoiceId: string;
  creditNoteId: string;
};

export function useBillingUserfulOnboarding() {
  return useMutation<{ staffBillingUserfulOnboarding: SalesActionResult }, UserfulOnboardingVars>(
    STAFF_BILLING_USERFUL_ONBOARDING,
  );
}

export type InvoiceStatusAction = 'paid' | 'void';

/** Stripe caps a metadata value at 500 chars; mirrored server-side. */
export const INVOICE_COMMENT_MAX = 500;

export type SetInvoiceStatusVars = {
  /** Stripe invoice id (`in_...`) or invoice number. */
  invoiceRef: string;
  action: InvoiceStatusAction;
  comment: string;
};

export type BillingInvoiceResult = {
  invoiceId: string;
  number?: string | null;
  previousStatus: string;
  status: string;
  /** Stripe minor units (cents for USD) — format at the render site. */
  total: number;
  currency: string;
  billingReason?: string | null;
  customerId?: string | null;
  message: string;
  /** False when the invoice was already in the target state. */
  changed: boolean;
};

export function useBillingSetInvoiceStatus() {
  return useMutation<
    { staffBillingSetInvoiceStatus: BillingInvoiceResult },
    SetInvoiceStatusVars
  >(STAFF_BILLING_SET_INVOICE_STATUS);
}

// ───────────────────── Create Hardware Only Invoice ──────────────────────

/**
 * Shipping regions — selects the `deviceShippings` rate branch server-side, and
 * whether Stripe computes US tax. NOT an address: the tool sends no ship-to
 * details, so tax geolocates from the Stripe customer's existing address.
 */
export type HardwareRegion = 'us' | 'international';

/** Hardware lines per invoice; mirrors MAX_HARDWARE_LINES server-side. */
export const HARDWARE_MAX_LINES = 20;

export type HardwarePrice = {
  currency: string;
  /** Minor units (cents). */
  amount: number;
};

export type HardwareCatalogProduct = {
  /** `deviceProducts._id`, e.g. `proMax`. */
  sku: string;
  /** `''` for a catalog-priced product; a `price_…` for a Stripe-priced config. */
  stripePriceId: string;
  /** `<catalog name>` or `<catalog name> (<price nickname>)`. */
  name: string;
  /** Only currencies the SKU actually has a usable price in. */
  prices: HardwarePrice[];
  /** False when no Stripe product is mapped for the SKU yet. */
  invoiceable: boolean;
  /**
   * Ships to the US only — kiosks, today. The picker drops these while the ship-to region
   * is International instead of offering an order the server would refuse.
   */
  usOnly: boolean;
};

export type HardwareShippingMethod = {
  id: string;
  name: string;
  /** Regions this method has rates for. */
  regions: HardwareRegion[];
};

export type HardwareCatalog = {
  products: HardwareCatalogProduct[];
  shippingMethods: HardwareShippingMethod[];
  currencies: string[];
};

export type HardwareLineVars = {
  sku: string;
  /** '' for a catalog-priced product; identifies WHICH configuration to bill. */
  stripePriceId: string;
  quantity: number;
};

/**
 * NOTE: no amounts. Every price is re-read from the Mongo catalog server-side,
 * so the preview the card renders is display-only and cannot affect billing.
 * Also no address and no discount — both are the reviewer's job on the draft.
 */
export type CreateHardwareInvoiceVars = {
  input: {
    stripeId: string;
    currency: string;
    region: HardwareRegion;
    lines: HardwareLineVars[];
    /** No shipping method: the server always uses Standard. */
    comment: string;
    /**
     * `custops_document.id` of the PO the attachment control filed against this customer.
     *
     * Sent so the link runs BOTH ways: the card patches the invoice onto the document, and
     * the server writes the document's console URL onto the Stripe invoice as
     * `optisigns_po_doc_url`. Omitted when nothing was attached, which is the common case.
     */
    documentId?: string;
  };
};

export type HardwareInvoiceResult = {
  invoiceId: string;
  /** Absent on drafts — Stripe assigns it at finalization. */
  number?: string | null;
  status: string;
  currency: string;
  /** All amounts are Stripe minor units — format at the render site. */
  subtotal: number;
  discountTotal: number;
  shippingTotal: number;
  tax: number;
  total: number;
  lineCount: number;
  dashboardUrl: string;
  automaticTax: boolean;
  companyName?: string | null;
  accountId?: string | null;
  /**
   * Scheduled finalization. THREE states, and the difference matters:
   *   undefined -> no window was requested (feature off). NOT a failure, show nothing.
   *   true      -> Stripe will finalize (and in live mode email) at `finalizesAt`.
   *   false     -> one was requested and Stripe did not take it. WARN the operator.
   * Always test `=== false`, never `!finalizeScheduled`, or the off state renders a
   * warning about a feature nobody enabled.
   */
  finalizeScheduled?: boolean | null;
  /** ISO 8601 UTC; render in the operator's local zone. */
  finalizesAt?: string | null;
  /** 'staff' (1h) or 'opticlaw' (12h) — why the deadline is what it is. */
  finalizeRule?: string | null;
  message: string;
};

/**
 * Catalog for the SKU / currency / shipping pickers. Roles are enforced
 * server-side, so a non-billing admin simply gets an error rather than a list.
 */
export function useBillingHardwareCatalog() {
  return useQuery<{ staffBillingHardwareCatalog: HardwareCatalog }>(
    STAFF_BILLING_HARDWARE_CATALOG,
    { fetchPolicy: 'cache-and-network' },
  );
}

export function useBillingCreateHardwareInvoice() {
  return useMutation<
    { staffBillingCreateHardwareInvoice: HardwareInvoiceResult },
    CreateHardwareInvoiceVars
  >(STAFF_BILLING_CREATE_HARDWARE_INVOICE);
}

// ─────────────────── Create Stripe Subscription ────────────────────

/** Staff-created deals are always annual; the server offers no other interval. */
export const SUBSCRIPTION_INTERVAL = 'yearly';

export type PlanCurrencyPrice = {
  currency: string;
  /**
   * Minor units, or null when the amount is only knowable inside Stripe — see
   * `autoConverted`. Render nothing rather than a zero.
   */
  amountMinor?: number | null;
  /**
   * True when this currency is served by a Stripe multi-currency price whose base is
   * USD. The catalog's USD figure is NOT what the customer pays, so the card must show
   * "billed at Stripe's rate" instead of a number.
   */
  autoConverted: boolean;
};

export type PlanCatalogPlan = {
  /** `productPlans.alias` — standard | proPlus | engage. */
  alias: string;
  name: string;
  /** Per screen, per year. */
  prices: PlanCurrencyPrice[];
};

export type PlanCatalogAddOn = {
  key: string;
  label: string;
  /** False = flat line, no screen count (Custom Domain). */
  quantified: boolean;
  /** Empty unless tiered (OptiSound: standard | pro). */
  tiers: string[];
  /** Plans that grant the entitlement; empty when unrestricted. */
  requiresPlan: string[];
  prices: PlanCurrencyPrice[];
  plans: string[];
};

export type PlanCatalog = {
  plans: PlanCatalogPlan[];
  addOns: PlanCatalogAddOn[];
  currencies: string[];
  interval: string;
};

/**
 * Countries whose sales tax is state/province-level, so `state` is required there.
 * Mirrors STATE_REQUIRED_COUNTRIES server-side.
 */
export const STATE_REQUIRED_COUNTRIES = ['US', 'CA'];

/** The billing address, in the shape the mutation takes. */
export type CustomerAddressVars = {
  /** ISO 3166-1 alpha-2 — a full country name breaks Stripe's tax lookup. */
  country: string;
  /** Street address (Stripe's `line1`). */
  line1: string;
  /** Suite / apt / floor. Optional — tax never uses it. */
  line2?: string;
  city: string;
  /** 2-letter code; required for US/CA. */
  state?: string;
  postalCode: string;
};

/** A customer's CURRENT address, used to prefill the form. */
export type StripeCustomerBilling = {
  stripeId: string;
  /** From the company doc — early confirmation the right `cus_` was typed. */
  companyName?: string | null;
  /** The account owner's email — what the console's customer bar leads with. */
  ownerEmail?: string | null;
  /**
   * The STRIPE customer's email — where Stripe sends this customer's invoices, and what the
   * Billing Email field prefills from. Not the same thing as `ownerEmail`: a customer is often
   * billed to a finance mailbox that never signs into the portal.
   */
  billingEmail?: string | null;
  accountId: string;
  country?: string | null;
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  /**
   * False when Stripe's existing address is not complete enough for tax — the card
   * then REQUIRES the fields, because Stripe would otherwise refuse the subscription.
   */
  hasAddress: boolean;
  /**
   * Raw Stripe customer metadata `staff-app-dunning-exclusion`: `true` (stopped), `false` (resumed), a hand-typed
   * value, or null when never set. Shown verbatim by the Dunning control.
   */
  dunningExclusion?: string | null;
  /** Raw `staff-app-dunning-exclusion-until-date` — a pause's last day, YYYY-MM-DD (Central). */
  dunningExclusionUntil?: string | null;
};

export type SubscriptionAddOnVars = {
  key: string;
  /** Licensed screens; omitted for non-quantified add-ons. */
  maxQuantity?: number;
  tier?: string;
};

/**
 * NOTE what is absent: no price ids, no amounts, and none of the Stripe ids Normal
 * Onboarding asks for. The server resolves prices from the plan catalog and CREATES
 * the subscription, item and invoice ids — so there is no id here to mistype.
 */
export type CreateStripeSubscriptionVars = {
  input: {
    stripeId: string;
    planFeature: string;
    subQty: number;
    currency: string;
    addOns?: SubscriptionAddOnVars[];
    /**
     * Omitted when the operator did not change the prefilled address — an unchanged
     * address costs no Stripe write.
     */
    billingAddress?: CustomerAddressVars;
    /**
     * Omitted when the operator did not change the prefilled email — an unchanged email
     * costs no Stripe write. ACCOUNT-WIDE when sent: it redirects every future invoice for
     * this customer, not only this subscription's first one.
     */
    billingEmail?: string;
    comment: string;
    /**
     * Devices to bill on the subscription's FIRST invoice, so the customer gets ONE bill.
     * Catalog references and counts only — never money; the server re-reads every amount.
     * Omitted entirely for a software-only onboarding.
     */
    deviceLines?: { sku: string; stripePriceId: string; quantity: number }[];
    /** `us | international` — the freight branch. Required iff deviceLines is present. */
    deviceRegion?: HardwareRegion;
    /**
     * `custops_document.id` of the PO the attachment control filed against this customer.
     *
     * Sent so the link runs BOTH ways: the card patches the invoice onto the document, and
     * the server writes the document's console URL onto the Stripe invoice as
     * `optisigns_po_doc_url`. Omitted when nothing was attached, which is the common case.
     */
    documentId?: string;
  };
};

export type SubscriptionLineResult = {
  key: string;
  label: string;
  /** The `si_…` Stripe assigned — shown so the operator can verify in Stripe. */
  subscriptionItemId: string;
  quantity: number;
};

export type StripeSubscriptionResult = {
  subscriptionId: string;
  /** Screens line item id → persisted as `payment.subscription_normal_id`. */
  planItemId: string;
  lines: SubscriptionLineResult[];
  status: string;
  currency: string;
  invoiceId?: string | null;
  /** Absent while the invoice is a draft. */
  invoiceNumber?: string | null;
  invoiceStatus?: string | null;
  /** Minor units, read back from the invoice Stripe built. */
  invoiceSubtotal: number;
  invoiceTax: number;
  invoiceTotal: number;
  /** Dashboard deep link to the draft invoice — the operator's next click. */
  invoiceUrl: string;
  /**
   * False = everything worked except writing the comment as the invoice MEMO. Milder
   * than `mongoUpdated: false`, but still worth showing: the memo carries the PO the
   * customer reads, and the first invoice finalizes on Stripe's schedule — which is now
   * an explicit deadline (see `invoiceFinalizesAt`), not "within the hour".
   */
  invoiceMemoSet: boolean;
  /** Same three-state contract as the hardware result's `finalizeScheduled`. */
  invoiceFinalizeScheduled?: boolean | null;
  invoiceFinalizesAt?: string | null;
  invoiceFinalizeRule?: string | null;
  subscriptionUrl: string;
  automaticTax: boolean;
  /**
   * False = Stripe succeeded, the company doc did NOT get written. The card must make
   * that loud: retrying would create a second subscription.
   */
  mongoUpdated: boolean;
  companyName?: string | null;
  accountId: string;
  message: string;
};

/**
 * Plans + add-ons for the Create Stripe Subscription pickers. Server-side this is a
 * Mongo-only read, so it resolves even when the Stripe key is unset.
 */
export function useBillingPlanCatalog() {
  return useQuery<{ staffBillingPlanCatalog: PlanCatalog }>(STAFF_BILLING_PLAN_CATALOG, {
    fetchPolicy: 'cache-and-network',
  });
}

/**
 * Look up a customer's current billing address. LAZY on purpose: it is fired when the
 * operator finishes typing a `cus_` id, not on mount — there is no customer to read
 * until then.
 */
export function useBillingStripeCustomer() {
  return useLazyQuery<{ staffBillingStripeCustomer: StripeCustomerBilling }, { stripeId: string }>(
    STAFF_BILLING_STRIPE_CUSTOMER,
    { fetchPolicy: 'network-only' },
  );
}

/** Wire names of the `DunningExclusionValue` enum; Stripe stores STOP 'true', PAUSE 'pause', RESUME 'false'. */
export type DunningExclusionWire = 'STOP' | 'PAUSE' | 'RESUME';

export type BillingDunningExclusionResult = {
  stripeId: string;
  accountId: string;
  companyName?: string | null;
  /** Stripe's read-back after the write. */
  dunningExclusion?: string | null;
  dunningExclusionUntil?: string | null;
};

/**
 * Billing Console — Dunning control. Writes ONLY Stripe customer metadata
 * `staff-app-dunning-exclusion`; nothing in Mongo, nothing in staff-app's own dunning.
 */
export function useBillingSetDunningExclusion() {
  return useMutation<
    { staffBillingSetDunningExclusion: BillingDunningExclusionResult },
    { stripeId: string; value: DunningExclusionWire; comment: string; untilDate?: string | null }
  >(STAFF_BILLING_SET_DUNNING_EXCLUSION);
}

export function useBillingCreateStripeSubscription() {
  return useMutation<
    { staffBillingCreateStripeSubscription: StripeSubscriptionResult },
    CreateStripeSubscriptionVars
  >(STAFF_BILLING_CREATE_STRIPE_SUBSCRIPTION);
}

// ─────────────────── PO / Invoice - Change Screens / Add-ons ────────────────────

/** One add-on the customer already holds, as Stripe has it. */
export type SubscriptionSnapshotAddOn = {
  key: string;
  label: string;
  tier?: string | null;
  quantity: number;
  /** The existing `si_…`, reused rather than retyped. */
  subscriptionItemId: string;
};

/**
 * The baseline for the change card.
 *
 * `blockedReason` is the field to check FIRST: when it is set, nothing else here is
 * meaningful and the card must refuse rather than render a form. It carries the exact
 * string "No activated Subscription" for the commonest case.
 */
export type SubscriptionSnapshot = {
  accountId: string;
  companyName?: string | null;
  /** The account owner's email — what the card leads with when there is no company name. */
  ownerEmail?: string | null;
  subscriptionId?: string | null;
  status?: string | null;
  currency?: string | null;
  planAlias?: string | null;
  planItemId?: string | null;
  /** What Stripe is billing — the number the delta is applied to. */
  stripeQuantity?: number | null;
  /** `limitation.maxQuantity` — the licensed figure, shown as "current screens". */
  mongoQuantity?: number | null;
  quantityDrift: boolean;
  /**
   * Already the human label — Stripe's own wording for whichever method this subscription
   * uses ("Email invoice to the customer to pay manually" / "Automatically charge a payment
   * method on file"). Both are served.
   */
  collectionLabel?: string | null;
  /**
   * True when this customer charges a card, so the change is wrapped in a temporary flip to
   * invoice terms (D17a): the proration is emailed to be paid manually and the subscription
   * goes straight back on automatic charge, so renewals are unaffected.
   *
   * The card warns on this and locks the proration choice to "bill immediately" — holding it
   * to the next renewal would charge the card weeks later instead. The server enforces both;
   * this is a courtesy.
   */
  collectionFlipRequired: boolean;
  addOns: SubscriptionSnapshotAddOn[];
  blockedReason?: string | null;
};

/**
 * NOTE what is absent: no subscription id (the server resolves it from the customer) and
 * no prices. `screensDelta` is a SIGNED DELTA — screens to add — while each add-on's
 * `maxQuantity` is the ABSOLUTE desired quantity. That asymmetry is deliberate; the card
 * shows it as "current 25, add 15 → 40" against add-on rows prefilled with what is held.
 */
export type ChangeSubscriptionVars = {
  input: {
    stripeId: string;
    screensDelta: number;
    addOns?: SubscriptionAddOnVars[];
    proration: 'IMMEDIATELY' | 'NEXT_INVOICE';
    /**
     * Omitted when the operator did not change the prefilled email — an unchanged email
     * costs no Stripe write. ACCOUNT-WIDE when sent: it redirects every future invoice for
     * this customer, not only this change's proration invoice.
     */
    billingEmail?: string;
    comment: string;
    /** SKUs + quantities only — the server re-reads every price from the catalog. */
    deviceLines?: HardwareLineVars[];
    /** `us` | `international`. Required iff `deviceLines` is present. */
    deviceRegion?: string;
    /**
     * `custops_document.id` of the PO the attachment control filed against this customer.
     *
     * Sent so the link runs BOTH ways: the card patches the invoice onto the document, and
     * the server writes the document's console URL onto the Stripe invoice as
     * `optisigns_po_doc_url`. Omitted when nothing was attached, which is the common case.
     */
    documentId?: string;
  };
};

export type SubscriptionChangeResult = {
  subscriptionId: string;
  previousScreens: number;
  newScreens: number;
  lines: SubscriptionLineResult[];
  status: string;
  currency: string;
  /** `immediate` | `next_invoice` — echoed back. */
  prorationApplied: string;
  /**
   * Absent on the next-invoice path, and absent when Stripe raised no prorated invoice —
   * so the card must not assume an invoice exists just because the change succeeded.
   */
  prorationInvoiceId?: string | null;
  prorationInvoiceUrl?: string | null;
  prorationSubtotal?: number | null;
  prorationTax?: number | null;
  prorationTotal?: number | null;
  invoiceMemoSet?: boolean | null;
  /**
   * Three states, same as the create card: null/undefined = feature off, false = requested
   * and Stripe did not take it, true = confirmed. `false` is INFORMATIONAL here: Stripe puts
   * its own ~1h deadline on a proration invoice, so it still sends — just not on our timing.
   */
  invoiceFinalizeScheduled?: boolean | null;
  invoiceFinalizesAt?: string | null;
  invoiceFinalizeRule?: string | null;
  subscriptionUrl: string;
  /**
   * Devices billed alongside the change. `0` with device lines requested is the partial
   * failure that matters: the subscription change IS live and the hardware was NOT billed —
   * `message` says to raise it with Create Hardware Only Invoice rather than re-submitting.
   */
  deviceItemsCreated: number;
  deviceAmount?: number | null;
  /**
   * WHICH invoice the hardware landed on. Equal to `prorationInvoiceId` on the immediate
   * path (one bill); a separate hardware draft on the next-invoice path.
   */
  deviceInvoiceId?: string | null;
  deviceInvoiceUrl?: string | null;
  /** False when Stripe changed but Mongo did not — do NOT re-submit; repair Mongo instead. */
  mongoUpdated: boolean;
  /** Absent when no flip was needed — the customer was already on invoice terms. */
  collectionFlipped?: boolean | null;
  /**
   * `false` is the loud case: the change is live and the customer has been left invoicing
   * EVERY renewal until someone sets the collection method back in Stripe. `message` carries
   * the instruction.
   */
  collectionRestored?: boolean | null;
  /** True when the recorded Stripe ids disagreed with Stripe and were corrected. */
  driftHealed: boolean;
  companyName?: string | null;
  accountId: string;
  message: string;
};

/**
 * Lazy on purpose, and `network-only`: it is fired on blur of the Stripe-id field, and a
 * cached snapshot would show a stale screen count — the number the operator's delta is
 * about to be added to.
 */
export function useBillingSubscriptionSnapshot() {
  return useLazyQuery<
    { staffBillingSubscriptionSnapshot: SubscriptionSnapshot },
    { stripeId: string }
  >(STAFF_BILLING_SUBSCRIPTION_SNAPSHOT, { fetchPolicy: 'network-only' });
}

export function useBillingChangeSubscription() {
  return useMutation<
    { staffBillingChangeSubscription: SubscriptionChangeResult },
    ChangeSubscriptionVars
  >(STAFF_BILLING_CHANGE_SUBSCRIPTION);
}

// ────────────────────────── Apply Coupon ───────────────────────────

/**
 * One coupon the operator may attach, as the picker sees it. Rows come from the
 * staff-owned catalog already filtered to the active ones — there is no inactive
 * row to render here, and no `includeInactive` argument to ask for one.
 */
export type CouponOption = {
  /** Catalog key, e.g. `NON-PROFIT-10`. The ONLY coupon handle the card sends back. */
  couponKey: string;
  label: string;
  /** Human terms — what the customer was actually promised. Free text on the row. */
  terms?: string | null;
  /**
   * True ⇒ the server refuses the apply without a VERIFIED, non-archived `tax_exempt`
   * document. Advisory here: it only decides whether the form ASKS for the certificate,
   * because the server re-resolves the flag from the catalog row on every call.
   */
  requiresTaxExemptDoc: boolean;
  /**
   * The Document Center tag a successful apply stamps on the certificate that justified it
   * (`non-profit` for NON-PROFIT-10), or absent when this coupon stamps none. Advisory in
   * the same sense as `requiresTaxExemptDoc`: it is here so the card can say what picking a
   * certificate will DO to that certificate. The server re-derives it from the coupon key.
   */
  grantDocumentTag?: string | null;
  /**
   * Display-only summary of what the coupon takes off, from the row's Stripe display
   * cache. Absent on a row saved before the cache existed — fall back to `terms`, and
   * never treat it as the money: Stripe holds the amounts.
   */
  discountSummary?: string | null;
  /** Shown so the operator can confirm the row against Stripe. Never sent as input. */
  stripeCouponId: string;
};

export type CouponCatalog = {
  coupons: CouponOption[];
  /**
   * True = the catalog TABLE is missing in this environment (migration 011 never
   * applied), which is a different message and a different fix from an environment
   * where nobody has added a row yet. Branch on it before saying "no coupons".
   */
  couponsUnavailable: boolean;
};

/**
 * The ONE discount Stripe holds on a customer, read back from Stripe rather than
 * mirrored from our own arithmetic.
 *
 * "One" is load-bearing: attaching a coupon replaces whatever was there and Stripe keeps
 * no history of what it displaced, which is why a non-null one refuses the apply and the
 * card renders it as a banner with a Remove button instead.
 */
export type BillingCustomerDiscount = {
  /** Stripe Discount id (`di_…`) — the discount, not the coupon. */
  id: string;
  couponId: string;
  couponName?: string | null;
  /** Rendered form of what it takes off, e.g. `10% off` / `$500.00 off`. */
  summary: string;
  /** Percentage coupon; mutually exclusive with `amountOffMinor`. */
  percentOff?: number | null;
  /** Stripe MINOR units (cents for USD) — format at the render site. */
  amountOffMinor?: number | null;
  /** Only meaningful alongside `amountOffMinor`. */
  currency?: string | null;
  /** `once` | `repeating` | `forever` — the outcome panel branches its scope copy on it. */
  duration: string;
  /** Set for `repeating` only. */
  durationInMonths?: number | null;
  /** ISO timestamp of Stripe's `discount.start`. */
  startedAt: string;
  /** ISO timestamp; `repeating` only — null for `once` and `forever`. */
  endsAt?: string | null;
};

/**
 * One `tax_exempt` document on the RESOLVED account, as the certificate picker sees it.
 * A deliberately narrow projection of the Document Center row: no filename, no version,
 * no download URL — this surface exists to choose an id, not to ship a certificate.
 */
export type BillingTaxExemptDocument = {
  /** `custops_document.id` — the value that goes back as `taxExemptDocumentId`. */
  id: string;
  title: string;
  /**
   * `tax_exempt` | `non_profit` — the gate accepts either, so the option label says which.
   */
  docType: string;
  /** valid | expiring_soon | expired | pending_future | invalid | archived. */
  status: string;
  /** `YYYY-MM-DD`, or null for a certificate with no expiry. */
  expirationDate?: string | null;
  /**
   * Null ⇒ nobody has verified it, so the server refuses it however valid it looks.
   * Unverified rows come back on purpose and render greyed rather than hidden: "get it
   * verified in the Document Center" is the operator's real next step.
   */
  verifiedAt?: string | null;
  /** Which state/authority the exemption covers — disambiguates an account's several. */
  jurisdiction?: string | null;
};

/**
 * Everything the card's customer panel renders, in one read keyed by the pasted `cus_`.
 *
 * One query rather than three because each part is a precondition the operator must SEE
 * before submitting: `companyName` proves the id is the intended tenant, `currentDiscount`
 * decides whether apply is offered at all, and `taxExemptDocuments` is the list the
 * server's document gate will be checked against.
 */
export type BillingCouponPreview = {
  stripeId: string;
  /** The account the SERVER resolved from `payment.stripe_id` — never client-supplied. */
  accountId: string;
  /** From the company doc — proof the `cus_` id is the intended customer. */
  companyName?: string | null;
  /** The name Stripe holds, so a mismatch with `companyName` is visible before submit. */
  stripeName?: string | null;
  /** Non-null ⇒ apply is refused; the card offers Remove discount instead. */
  currentDiscount?: BillingCustomerDiscount | null;
  /**
   * The customer's Stripe Tax status TODAY (`none` | `exempt` | `reverse`), so the card can say
   * what applying will do to it before the operator commits.
   */
  taxStatus?: string | null;
  taxExemptDocuments: BillingTaxExemptDocument[];
  /**
   * False = the Document Center binding is unconfigured in this environment, so an empty
   * list means "cannot look", not "this account has no certificate".
   */
  documentsAvailable: boolean;
};

/**
 * NOTE what is absent: no Stripe coupon id, no percent, no amount. The card sends a
 * catalog KEY and the server resolves the coupon, its terms and its tax-exempt flag — so
 * there is no coupon here to tamper with and no gate to talk the server out of. Also
 * absent: `replaceExisting`. An existing discount is a refusal, not an overwrite.
 */
export type ApplyCouponVars = {
  input: {
    stripeId: string;
    /** Catalog key, e.g. `NON-PROFIT-10`. NOT a Stripe coupon id. */
    couponKey: string;
    /** Sent only when the chosen coupon is flagged — the server demands it then. */
    taxExemptDocumentId?: string;
    comment: string;
  };
};

/** Flat, matching the other single-purpose billing mutations. */
export type RemoveCouponVars = {
  stripeId: string;
  comment: string;
};

/**
 * Returned by BOTH apply and remove, so one outcome panel renders either. Apply resolves
 * with `discount` set and `previousCouponId` null; remove resolves with `discount: null`
 * and `previousCouponId` naming what was taken away — the only record of it, since Stripe
 * keeps no history of a removed discount.
 */
export type BillingCouponResult = {
  stripeId: string;
  accountId: string;
  companyName?: string | null;
  /** The discount now on the customer, read BACK from Stripe. Null after a remove. */
  discount?: BillingCustomerDiscount | null;
  /** The coupon id a remove took off. Null on apply — this tool never replaces. */
  previousCouponId?: string | null;
  /** Echoed so the operator can see WHICH certificate justified the discount. */
  taxExemptDocumentId?: string | null;
  taxExemptDocumentTitle?: string | null;
  /**
   * False = everything worked except the best-effort back-reference onto the certificate.
   * The discount exists either way, so the card says so rather than implying the link is
   * there — retrying would be a second apply, not a repair.
   */
  documentLinked: boolean;
  /**
   * The tag the apply stamped on that certificate (`non-profit`). Absent when the coupon
   * carries no tag, when no certificate was involved, or when a tag WAS due and the write
   * did not land — the `message` distinguishes the last case, as it does for
   * `documentLinked`.
   */
  documentTagApplied?: string | null;
  /**
   * The customer's Stripe **Tax status** after the write (`none` | `exempt` | `reverse`), read
   * back from Stripe rather than echoed from what was sent.
   */
  taxStatus?: string | null;
  /**
   * True when THIS request set it to exempt. False covers four cases the `message` tells apart:
   * no certificate was involved, it was already exempt, it is `reverse` and was left alone, or
   * the write did not take. Always false on a removal — taking a discount away does not make a
   * non-profit taxable.
   */
  taxStatusChanged: boolean;
  /** Stripe dashboard deep link to the CUSTOMER, built server-side. Rendered verbatim. */
  customerUrl: string;
  message: string;
};

/**
 * Catalog behind the coupon picker. Postgres-only server-side, so it resolves even when
 * the Stripe key is unset — same as the plan catalog is Mongo-only.
 */
export function useBillingCouponCatalog() {
  return useQuery<{ staffBillingCouponCatalog: CouponCatalog }>(STAFF_BILLING_COUPON_CATALOG, {
    fetchPolicy: 'cache-and-network',
  });
}

/**
 * Look up the customer the coupon would be applied to. LAZY on purpose: it is fired when
 * the operator finishes typing a `cus_` id, not on mount. `network-only` is not tidiness
 * — a cached preview could show "no discount" for a customer who has one since, and this
 * read is the precondition the apply is gated on.
 */
export function useBillingCouponPreview() {
  return useLazyQuery<{ staffBillingCouponPreview: BillingCouponPreview }, { stripeId: string }>(
    STAFF_BILLING_COUPON_PREVIEW,
    { fetchPolicy: 'network-only' },
  );
}

export function useBillingApplyCoupon() {
  return useMutation<{ staffBillingApplyCoupon: BillingCouponResult }, ApplyCouponVars>(
    STAFF_BILLING_APPLY_COUPON,
  );
}

export function useBillingRemoveCoupon() {
  return useMutation<{ staffBillingRemoveCoupon: BillingCouponResult }, RemoveCouponVars>(
    STAFF_BILLING_REMOVE_COUPON,
  );
}
